import React, { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, Grid, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import Modal from "@/components/Modal";
import Select from "@/components/Select";
import Button from "@/components/Button";
import GenericAlertModal from "@/components/AlertModal";
import { useAppTheme } from "@/utils/theme";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useSaveClosure } from "@/hooks/useSiteOfficeHours";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { ErrorResponse } from "@/domain/error.interface";
import { SiteOfficeClosure } from "@/domain/site-office-hours.interface";

// AC04: order — "No active closure" first, then Emergency, Weather, Holiday
const CLOSURE_REASONS = [
  { key: "No active closure", value: "No active closure" },
  { key: "Emergency", value: "Emergency" },
  { key: "Weather", value: "Weather" },
  { key: "Holiday", value: "Holiday" },
];

const NO_ACTIVE_CLOSURE = "No active closure";

const parseNaiveDatetime = (iso: string) => dayjs(iso.replace("Z", ""));
const toNaiveIso = (dt: Dayjs) => dt.format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";

const schema = yup.object({
  reason: yup.string().required("Reason is required"),
  notes: yup.string(),
  // AC08: dates required only when reason is an active closure
  visibilityStartDate: yup
    .mixed<Dayjs>()
    .nullable()
    .when("reason", {
      is: (r: string) => r !== NO_ACTIVE_CLOSURE,
      then: (s) =>
        s
          .required("Start date is required")
          .test(
            "is-valid",
            "Start date is required",
            (val) => dayjs.isDayjs(val) && val.isValid()
          ),
      otherwise: (s) => s.nullable(),
    }),
  // AC09: end must be after start
  visibilityEndDate: yup
    .mixed<Dayjs>()
    .nullable()
    .when("reason", {
      is: (r: string) => r !== NO_ACTIVE_CLOSURE,
      then: (s) =>
        s
          .required("End date is required")
          .test(
            "is-valid",
            "End date is required",
            (val) => dayjs.isDayjs(val) && val.isValid()
          )
          .test(
            "after-start",
            "End date must be after start date",
            function (val) {
              const start = this.parent.visibilityStartDate as Dayjs | null;
              if (!dayjs.isDayjs(val) || !dayjs.isDayjs(start)) return true;
              return val.isAfter(start);
            }
          ),
      otherwise: (s) => s.nullable(),
    }),
});

type FormValues = yup.InferType<typeof schema>;

type Props = {
  open: boolean;
  onClose: () => void;
  programId: string;
  orgUnitId: string;
  existingClosure?: SiteOfficeClosure | null;
};

const SetClosureModal: FC<Props> = ({
  open,
  onClose,
  programId,
  orgUnitId,
  existingClosure,
}) => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const createAuditLog = useCreateAuditlog();
  const saveClosureMutation = useSaveClosure();

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<FormValues | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      reason: NO_ACTIVE_CLOSURE,
      notes: "",
      visibilityStartDate: null as unknown as Dayjs,
      visibilityEndDate: null as unknown as Dayjs,
    },
  });

  const watchedReason = watch("reason");
  const isActiveClosure = watchedReason && watchedReason !== NO_ACTIVE_CLOSURE;

  // AC02: pre-populate with existing closure; AC03: default to "No active closure" when none
  useEffect(() => {
    if (!open) return;

    if (existingClosure) {
      reset({
        reason: existingClosure.reason,
        notes: existingClosure.notes ?? "",
        visibilityStartDate: parseNaiveDatetime(
          existingClosure.visibilityStartDate
        ),
        visibilityEndDate: parseNaiveDatetime(
          existingClosure.visibilityEndDate
        ),
      });
    } else {
      reset({
        reason: NO_ACTIVE_CLOSURE,
        notes: "",
        visibilityStartDate: null as unknown as Dayjs,
        visibilityEndDate: null as unknown as Dayjs,
      });
    }
  }, [open]);

  const handleClose = () => {
    reset();
    onClose();
  };

  // AC10: validate first, then show confirmation dialog
  const handleRequestSave = (data: FormValues) => {
    setPendingPayload(data);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingPayload) return;
    setShowConfirm(false);

    const data = pendingPayload;
    const isActive = data.reason !== NO_ACTIVE_CLOSURE;

    // AC05: when "No active closure", clear notes and dates (send current time as placeholder)
    const now = toNaiveIso(dayjs());
    const payload = {
      programId,
      orgUnitId,
      userId: userInfo?._id as string,
      reason: data.reason,
      notes: isActive ? data.notes || null : null,
      visibilityStartDate: isActive
        ? toNaiveIso(data.visibilityStartDate as Dayjs)
        : now,
      visibilityEndDate: isActive
        ? toNaiveIso(data.visibilityEndDate as Dayjs)
        : now,
    };

    try {
      await saveClosureMutation.mutateAsync(payload, {
        onSuccess: () => {
          handleClose();
          showSnackBar("Closure saved successfully.", "success");
          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.SETTINGS,
            option: OPTIONS.CLOSURE_AND_HOURS,
            actionCode: ACTION_CODE.WEB_SETTINGS_CLOSURE_SAVE,
            action: "When saving",
            detail: `Saved site closure: ${data.reason}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id as string,
            programId,
            orgUnitId,
          });
        },
        onError: (error: ErrorResponse) => {
          showSnackBar(
            (Array.isArray(error.message) ? error.message[0] : error.message) ||
              error.error ||
              "An error occurred"
          );
        },
      });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Modal
        open={open}
        onClose={handleClose}
        isPending={saveClosureMutation.isPending}
        title="Set Closure"
        theme={theme}
        size={{ width: "100%", minHeight: 500, maxHeight: 660, maxWidth: 480 }}
        content={
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2.5}>
              {/* Reason */}
              <Grid item xs={12}>
                <Typography
                  variant="bodyLarge"
                  fontWeight={700}
                  color="primary"
                >
                  Reason
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <Select
                      id="closure-reason"
                      label="Closure reason"
                      data={CLOSURE_REASONS}
                      value={field.value ?? ""}
                      handleChange={(val) => field.onChange(val)}
                      error={!!errors.reason}
                      errorMessage={errors.reason?.message}
                      required
                    />
                  )}
                />
              </Grid>

              {/* AC05/AC06: notes disabled when "No active closure" */}
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Additional notes"
                      placeholder="Additional notes"
                      multiline
                      rows={3}
                      fullWidth
                      disabled={!isActiveClosure}
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />
              </Grid>

              {/* Visibility */}
              <Grid item xs={12}>
                <Typography
                  variant="bodyLarge"
                  fontWeight={700}
                  color="primary"
                >
                  Scheduled Closure Visibility (auto-expire)
                </Typography>
              </Grid>

              {/* AC05/AC06: date pickers disabled when "No active closure" */}
              <Grid item xs={12}>
                <Controller
                  name="visibilityStartDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="From date & time"
                      value={field.value ?? null}
                      onChange={(val) => field.onChange(val)}
                      disabled={!isActiveClosure}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.visibilityStartDate,
                          helperText: errors.visibilityStartDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="visibilityEndDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="To date & time"
                      value={field.value ?? null}
                      onChange={(val) => field.onChange(val)}
                      disabled={!isActiveClosure}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.visibilityEndDate,
                          helperText: errors.visibilityEndDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        }
        actions={
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variantType="cancel"
              onClick={handleClose}
              disabled={saveClosureMutation.isPending}
              label="CANCEL"
            />
            <Button
              variantType="save"
              onClick={handleSubmit(handleRequestSave)}
              disabled={saveClosureMutation.isPending}
              isLoading={saveClosureMutation.isPending}
              label="ACCEPT"
            />
          </Box>
        }
      />

      {/* AC10: confirmation dialog */}
      {showConfirm && (
        <GenericAlertModal
          title="Save Closure"
          description="Are you sure you want to save the closure configuration for the selected program and site?"
          theme={theme}
          onCancel={() => setShowConfirm(false)}
          onClick={handleConfirmSave}
          submitText="Confirm"
          cancelText="Cancel"
        />
      )}
    </LocalizationProvider>
  );
};

export default SetClosureModal;
